import React from 'react';

import { RenderChart } from './stockChart';
import { ForeCastType } from '../../../shared/constants/api';

interface IProps {
	chartType: string;
	chartDetails: any;
	advanceChartConfig: any;
	ele: any;
	cardIndex: number;
	materialIndex?: number;
	strokeColor: any;
	colorList: any;
	data?: any;
	selectedKeys?: string[];
	barSeriesStartIndexMap?: Record<string, number>;
}

const CommonChart: React.FC<IProps> = (props) => {
	const { advanceChartConfig, chartType, ele, chartDetails, cardIndex, strokeColor, colorList, data, selectedKeys,barSeriesStartIndexMap } = props;

	// Add null checks for data and ele
	if (!data || !Array.isArray(data) || !ele) {
		console.warn('Invalid data or ele object provided to CommonChart');
		return null;
	}

	// Get the start index and number of fields for this chart
	const startIndex = (barSeriesStartIndexMap && barSeriesStartIndexMap[`${ele.id}`]) || 0;
	const numFields = Array.isArray(ele.fields) && ele.fields.length > 0 ? ele.fields.length : 1;
	
	// Filter actual price data by checking all data indices that belong to this chart
	const actualPriceData = data.filter((item: any) => {
		if (!item) return false;
		// Check if any of the data indices for this chart have actual price data
		for (let i = 0; i < numFields; i++) {
			const dataIndex = startIndex + i;
			const key = `cv${ele.symbol}-${ele.boerse}-${dataIndex}`;
			if (item[key]?.type === ForeCastType.ACTUAL_PRICE) {
				return true;
			}
		}
		return false;
	});

	const actualPriceLastData = actualPriceData[actualPriceData.length - 1] || {};
	const lastActualPriceDate = actualPriceLastData?.date || new Date();
	const selectedValueKeys = selectedKeys && selectedKeys.length > 0 ? selectedKeys : ['l'];
	
	// Check if forecast sources exist - handle both direct sources and nested structure
	const forecastSources = chartDetails?.sources || (chartDetails && chartDetails[`${ele.symbol}-${ele.boerse}-0`]?.sources);
	const hasValidColorList = colorList && Object.keys(colorList).length > 0;
	
	return (
		<>
			{!chartDetails.isIntraday && (
				<>
					{selectedValueKeys.length > 1 ? (
						<RenderChart
							type={chartType}
							yAccessors={selectedValueKeys.map((key) => {
								// Compute global start index for this material
							   const startIndex = (barSeriesStartIndexMap && barSeriesStartIndexMap[`${ele.id}`]) || 0;
								// Local index of the field within this material
								const localIdx = Array.isArray(ele.fields) ? Math.max(0, ele.fields.indexOf(key)) : 0;
								// Global series index in flattened API keys
								const seriesIdx = startIndex + localIdx;
								return (d: any) => {
									const seriesData = d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`];
									if (seriesData?.type === ForeCastType.ACTUAL_PRICE || advanceChartConfig.chartSettings.length > 1) {
										return seriesData?.l;
									}
								};
							})}
							fills={selectedValueKeys.map((key) => colorList[key] || ele.color)}
							stroke={selectedValueKeys.map((key) => colorList[key] || ele.color)}
						/>
					) : (
						<RenderChart
							type={chartType}
							yAccessor={(d: any) => {
								const key = selectedValueKeys[0] || 'l';
								const startIndex = (barSeriesStartIndexMap && barSeriesStartIndexMap[`${ele.id}`]) || 0;
								const localIdx = Array.isArray(ele.fields) ? Math.max(0, ele.fields.indexOf(key)) : 0;
								const seriesIdx = startIndex + localIdx;
								const seriesData = d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`];
								if (seriesData?.type === ForeCastType.ACTUAL_PRICE || advanceChartConfig.chartSettings.length > 1) {
									return seriesData?.l;
								}
							}}
							fill={`url(#MyGradient${ele.symbol}-${ele.boerse}${ele.id}-${cardIndex})`}
							stroke={ele.color}
						/>
					)}
					{chartType !== 'bar' && advanceChartConfig.chartSettings.length === 1 && (
						<>
							{forecastSources && hasValidColorList && (
								<RenderChart
									type={chartType}
									yAccessors={[
										...Object.keys(colorList).map((key) => (d: any) => {
											const dataIndex = startIndex;
											const chartData = d[`cv${ele.symbol}-${ele.boerse}-${dataIndex}`];
											const sourceData = chartData?.source_data;
											
											// Return forecast data or connect from last actual price
											if (chartData?.type === ForeCastType.FORECASTED_PRICE && sourceData?.[key]) {
												return sourceData[key]?.l;
											} else if (d?.date === lastActualPriceDate) {
												// Connect the last actual price point to forecast
												return actualPriceLastData[`cv${ele.symbol}-${ele.boerse}-${dataIndex}`]?.l;
											}
											return undefined;
										})
									]}
									fills={[
										...Object.keys(colorList).map((key, idx) => {
											return `url(#MyGradientListForecast${idx})`;
										})
									]}
									stroke={strokeColor}
									chartDetails={chartDetails}
								/>
							)}
						</>
					)}
				</>
			)}
			{chartDetails.isIntraday && chartType !== 'bar' && (
				<RenderChart
					type={chartType}
					yAccessor={(d: any) => {
						// Intraday: use global start index + local field index
						const key = selectedValueKeys[0] || 'l';
						const startIndex = (barSeriesStartIndexMap && barSeriesStartIndexMap[`${ele.id}`]) || 0;
						const localIdx = Array.isArray(ele.fields) ? Math.max(0, ele.fields.indexOf(key)) : 0;
						const seriesIdx = startIndex + localIdx;
						return d[`cv${ele.symbol}-${ele.boerse}-${seriesIdx}`]?.l;
					}}
					fill={`url(#MyGradient${ele.symbol}-${ele.boerse}${ele.id}-${cardIndex})`}
					stroke={ele.color}
				/>
			)}
		</>
	);
};

export default CommonChart;

